import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    adminListAllOrders,
    adminPackingSlipUrl,
    clearAdminToken,
    getAdminToken,
} from "../lib/api";
import { toast } from "sonner";
import {
    Loader2,
    ExternalLink,
    FileText,
    LogOut,
    RefreshCw,
} from "lucide-react";

const StatusPill = ({ status, paymentStatus }) => {
    let color = "bg-[#E5E0D8] text-[#5C6A79]";
    let label = status || "pending";
    if (paymentStatus === "paid") {
        color = "bg-emerald-100 text-emerald-800";
        label = "Paid";
    } else if (paymentStatus === "unpaid") {
        color = "bg-amber-100 text-amber-800";
        label = "Unpaid";
    }
    return (
        <span
            className={`inline-block text-[10px] tracking-[0.15em] uppercase px-2 py-1 rounded-sm ${color}`}
        >
            {label}
        </span>
    );
};

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const navigate = useNavigate();

    const load = async () => {
        try {
            const data = await adminListAllOrders();
            setOrders(data);
        } catch (err) {
            if (err?.response?.status === 401) {
                clearAdminToken();
                navigate("/admin");
                return;
            }
            toast.error("Could not load orders");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        if (!getAdminToken()) {
            navigate("/admin");
            return;
        }
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refresh = () => {
        setRefreshing(true);
        load();
    };

    const logout = () => {
        clearAdminToken();
        navigate("/admin");
    };

    const openLabel = (order) => {
        if (order.shippo_label_url) window.open(order.shippo_label_url, "_blank");
        else toast.info("Label not ready yet");
    };

    const openSlip = (order) => {
        const url = adminPackingSlipUrl(order.session_id);
        const token = getAdminToken();
        // Fetch as blob with auth, open in new tab
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const objectUrl = URL.createObjectURL(blob);
                window.open(objectUrl, "_blank");
            })
            .catch(() => toast.error("Could not open packing slip"));
    };

    return (
        <div
            data-testid="page-admin-orders"
            className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 py-10 md:py-16"
        >
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
                <div>
                    <p className="text-[11px] tracking-[0.35em] uppercase text-[#9E5A35] mb-3">
                        Admin
                    </p>
                    <h1 className="font-serif-display text-4xl md:text-5xl text-[#0A2342] leading-[1.05]">
                        Orders
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid="admin-refresh-btn"
                        onClick={refresh}
                        disabled={refreshing}
                        className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#0A2342] border border-[#E5E0D8] px-4 py-2 rounded-sm hover:border-[#0A2342] disabled:opacity-40"
                    >
                        <RefreshCw
                            size={13}
                            className={refreshing ? "animate-spin" : ""}
                        />
                        Refresh
                    </button>
                    <button
                        data-testid="admin-logout-btn"
                        onClick={logout}
                        className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-[#5C6A79] border border-[#E5E0D8] px-4 py-2 rounded-sm hover:text-[#0A2342] hover:border-[#0A2342]"
                    >
                        <LogOut size={13} /> Logout
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-20 text-[#5C6A79]">
                    <Loader2 className="animate-spin mx-auto mb-2" size={20} />
                    Loading orders…
                </div>
            ) : orders.length === 0 ? (
                <div
                    data-testid="admin-empty-state"
                    className="text-center py-20 bg-white border border-[#E5E0D8] rounded-sm"
                >
                    <div className="font-serif-display text-2xl text-[#0A2342] mb-2">
                        No orders yet
                    </div>
                    <p className="text-sm text-[#5C6A79]">
                        Once a customer completes checkout, their order will
                        appear here.
                    </p>
                </div>
            ) : (
                <div className="bg-white border border-[#E5E0D8] rounded-sm overflow-hidden">
                    <table className="w-full text-sm" data-testid="admin-orders-table">
                        <thead className="bg-[#FBF9F6] border-b border-[#E5E0D8]">
                            <tr className="text-[10px] tracking-[0.22em] uppercase text-[#5C6A79]">
                                <th className="text-left px-4 py-4 font-medium">Date</th>
                                <th className="text-left px-4 py-4 font-medium">Customer</th>
                                <th className="text-left px-4 py-4 font-medium">Items</th>
                                <th className="text-left px-4 py-4 font-medium">Total</th>
                                <th className="text-left px-4 py-4 font-medium">Status</th>
                                <th className="text-left px-4 py-4 font-medium">Tracking</th>
                                <th className="text-right px-4 py-4 font-medium">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((o) => (
                                <tr
                                    key={o.session_id}
                                    data-testid={`admin-order-row-${o.session_id.slice(0, 12)}`}
                                    className="border-b border-[#E5E0D8] last:border-b-0 hover:bg-[#FBF9F6]/50"
                                >
                                    <td className="px-4 py-4 text-[#5C6A79] whitespace-nowrap">
                                        {(o.created_at || "").slice(0, 10)}
                                    </td>
                                    <td className="px-4 py-4">
                                        <div className="text-[#0A2342] font-medium">
                                            {o.shipping_address?.name ||
                                                o.customer_email ||
                                                "—"}
                                        </div>
                                        <div className="text-xs text-[#5C6A79]">
                                            {o.customer_email || ""}
                                        </div>
                                        {o.shipping_address?.city && (
                                            <div className="text-xs text-[#5C6A79]">
                                                {o.shipping_address.city},{" "}
                                                {o.shipping_address.state}{" "}
                                                {o.shipping_address.zip}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-4 text-[#5C6A79]">
                                        {(o.items || [])
                                            .map(
                                                (i) =>
                                                    `${i.quantity}× ${i.product_id}`,
                                            )
                                            .join(", ")}
                                    </td>
                                    <td className="px-4 py-4 text-[#0A2342] whitespace-nowrap">
                                        ${(o.amount || 0).toFixed(2)}
                                        {o.free_shipping && (
                                            <div className="text-[10px] text-[#9E5A35] uppercase tracking-[0.15em]">
                                                Free ship
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-4">
                                        <StatusPill
                                            status={o.status}
                                            paymentStatus={o.payment_status}
                                        />
                                    </td>
                                    <td className="px-4 py-4 text-xs text-[#5C6A79]">
                                        {o.shippo_tracking_number || "—"}
                                    </td>
                                    <td className="px-4 py-4 text-right whitespace-nowrap">
                                        <button
                                            data-testid={`admin-label-btn-${o.session_id.slice(0, 12)}`}
                                            onClick={() => openLabel(o)}
                                            disabled={!o.shippo_label_url}
                                            className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-[#0A2342] hover:text-[#9E5A35] mr-4 disabled:opacity-30 disabled:cursor-not-allowed"
                                        >
                                            <ExternalLink size={12} /> Label
                                        </button>
                                        <button
                                            data-testid={`admin-slip-btn-${o.session_id.slice(0, 12)}`}
                                            onClick={() => openSlip(o)}
                                            className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-[#0A2342] hover:text-[#9E5A35]"
                                        >
                                            <FileText size={12} /> Slip
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default AdminOrders;
